import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { Link } from 'react-router-dom';

import ConfirmationDialog from '@Components/ConfirmationDialog';
import { useDeleteInviteCode } from '@Hooks/useDeleteInviteCode';
import { useInviteCodeUsage } from '@Hooks/useInviteCodeUsage';
import type { RegistrationCode } from '@Types/registrationCode';

export interface DeleteInviteCodeDialogProps {
  inviteCode: RegistrationCode | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted?: () => void;
}

export default function DeleteInviteCodeDialog({
  inviteCode,
  open,
  onOpenChange,
  onDeleted,
}: DeleteInviteCodeDialogProps) {
  const deleteMutation = useDeleteInviteCode();
  const usage = useInviteCodeUsage({
    id: inviteCode?.id ?? '',
    enabled: open && !!inviteCode,
  });

  const activeSubscriptionCount = usage.data?.activeSubscriptionCount ?? 0;
  const partnerReferralCodes = usage.data?.partnerReferralCodes ?? [];

  const handleConfirm = async () => {
    if (!inviteCode) return;

    try {
      await deleteMutation.mutateAsync({ id: inviteCode.id });
      toastSuccess(`"${inviteCode.code}" has been deleted.`);
      onOpenChange(false);
      onDeleted?.();
    } catch (err) {
      toastError(
        err instanceof Error ? err.message : 'Failed to delete Invite Code',
      );
    }
  };

  return (
    <ConfirmationDialog
      open={open}
      onOpenChange={onOpenChange}
      onSubmit={handleConfirm}
      match={inviteCode?.code ?? ''}
      title="Delete Invite Code"
      description={`Are you sure you want to delete "${inviteCode?.code}"? New signups can no longer use it, and the code name stays reserved.`}
      matchLabel="Type the invite code to confirm"
      submitLabel="Delete"
      isLoading={deleteMutation.isPending}
      variant="destructive"
    >
      <div className="space-y-3 text-sm">
        {usage.isLoading && (
          <p className="text-zinc-500">Checking where this code is used...</p>
        )}
        {usage.isError && (
          <p className="text-red-700">
            Unable to check where this code is used: {usage.error.message}
          </p>
        )}
        {usage.data && (
          <p>
            Existing members keep their renewal discounts.
            {activeSubscriptionCount > 0 &&
              ` ${activeSubscriptionCount} active ${activeSubscriptionCount === 1 ? 'member uses' : 'members use'} this code.`}
          </p>
        )}
        {partnerReferralCodes.length > 0 && (
          <div>
            <p>
              These partner codes link to it and stop offering it once it is
              deleted:
            </p>
            <ul className="mt-1 list-disc pl-5">
              {partnerReferralCodes.map((partnerReferralCode) => (
                <li key={partnerReferralCode.id}>
                  <Link
                    to={`/partner-referral-codes/${partnerReferralCode.id}`}
                    className="text-primary font-mono hover:underline"
                  >
                    {partnerReferralCode.code}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </ConfirmationDialog>
  );
}
